import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Calendar, RefreshCw } from "lucide-react";
import Card from "../components/Card";
import { BRAND } from "../components/brand";

export default function MonthwiseCustomersChart({ retailer_id, selected_user_id }) {
  const [data, setData] = useState([]);
  console.log("user count: ", data)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  // 🔹 Auth
  const authUserString = sessionStorage.getItem("authUserData");
  const authUser = JSON.parse(authUserString);
  const userRole = authUser?.data?.[0]?.role;
  const userId = authUser?.data?.[0]?.id;
  const company_id = authUser?.data?.[0]?.company_id || 26;

  // 🔹 Date range for month
  const getMonthRange = (monthString) => {
    const [year, month] = monthString.split("-").map(Number);
    const fromDate = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0).getDate();
    const toDate = new Date(year, month - 1, lastDay);
    return {
      from: fromDate.toISOString().split("T")[0],
      to: toDate.toISOString().split("T")[0],
      lastDay,
    };
  };

  // 🔹 Fill full month for empty days
  const mergeWithFullMonth = (chartData, lastDay) => {
    const fullMonth = Array.from({ length: lastDay }, (_, i) => ({
      day: i + 1,
      recommended: 0,
      chalte_chalte: 0,
      total_customers: 0,
    }));

    chartData.forEach((d) => {
      const day = new Date(d.date).getDate();
      const index = fullMonth.findIndex((x) => x.day === day);
      if (index !== -1) {
        fullMonth[index].recommended = Number(d.total_type_1) || 0;
        fullMonth[index].chalte_chalte = Number(d.total_type_3) || 0;
        fullMonth[index].total_customers = Number(d.total_customers) || 0;
      }
    });

    return fullMonth;
  };

  // 🔹 Fetch Monthly Customer Chart (roles 2, 5)
  const fetchCustomerChart = async () => {
    const { from, to, lastDay } = getMonthRange(selectedMonth);
    let url = `https://goelectronix.in/api/v1/get-customers-chart?from_date=${from}&to_date=${to}&company_id=${company_id}`;

    if (userRole === 3) url += `&cnf_id=${userId}`;
    else if (userRole === 4) url += `&ss_id=${userId}`;
    else if (userRole === 5 || userRole === 6) url += `&dist_id=${userId}`;

    if (retailer_id) url += `&retailer_id=${retailer_id}`;
    if (selected_user_id) url += `&user_id=${selected_user_id}`;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(url);
      const response = res.data;
      const formatted = response?.chart_data?.length
        ? mergeWithFullMonth(response.chart_data, lastDay)
        : Array.from({ length: lastDay }, (_, i) => ({
            day: i + 1,
            recommended: 0,
            chalte_chalte: 0,
            total_customers: 0,
          }));
      setData(formatted);
      setSummary(response.summary || null);
    } catch (err) {
      console.error("Error fetching customer chart:", err);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch User Registration Chart (roles 3, 4)
  const fetchUserRegistrationChart = async () => {
    setLoading(true);
    setError(null);
    try {
      let apiUrl = "";
      if (userRole === 3)
        apiUrl = `https://goelectronix.in/api/v1/org_user?role=4&cnf_id=${userId}&page=1&per_page=100000`;
      else if (userRole === 4)
        apiUrl = `https://goelectronix.in/api/v1/org_user?role=5&ss_id=${userId}&page=1&per_page=100000`;

      const res = await axios.get(apiUrl);
      const users = res.data?.data || [];

      // Group by registration date
      const grouped = {};
      users.forEach((u) => {
        const date = new Date(u.created_at).toISOString().split("T")[0];
        grouped[date] = (grouped[date] || 0) + 1;
      });

      const { lastDay } = getMonthRange(selectedMonth);
      const chartData = Array.from({ length: lastDay }, (_, i) => {
        const date = new Date(
          `${selectedMonth}-${String(i + 1).padStart(2, "0")}`
        )
          .toISOString()
          .split("T")[0];
        return {
          day: i + 1,
          registered_users: grouped[date] || 0,
        };
      });

      setData(chartData);
      setSummary({ total_registered: res.data?.total || 0 });
    } catch (err) {
      console.error("Error fetching registration chart:", err);
      setError("Failed to load registration data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === 2 || userRole === 5) fetchCustomerChart();
    else if (userRole === 3 || userRole === 4) fetchUserRegistrationChart();
  }, [selectedMonth, retailer_id, selected_user_id]);

  // 🔹 Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const first = payload[0];
    const data = first.payload;
    return (
      <div className="bg-white border border-gray-300 shadow-md p-2 rounded-md text-xs">
        <p className="font-semibold text-gray-800">Day: {label}</p>
        {data.recommended !== undefined && (
          <>
            <p className="text-blue-600">Recommended: {data.recommended}</p>
            <p className="text-indigo-600">
              Chalte Chalte: {data.chalte_chalte}
            </p>
            <p className="text-gray-700 font-semibold">
              Total: {data.total_customers}
            </p>
          </>
        )}
        {data.registered_users !== undefined && (
          <p className="text-blue-600 font-semibold">
            Registered Users: {data.registered_users}
          </p>
        )}
      </div>
    );
  };

  const chartTitle =
    userRole === 3 || userRole === 4
      ? "User Registration Chart"
      : "Month-wise Customers";

  return (
    <Card title={chartTitle}>
      {/* 🔹 Month Selector */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-blue-600" />
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() =>
            userRole === 2 || userRole === 5
              ? fetchCustomerChart()
              : fetchUserRegistrationChart()
          }
          className="inline-flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* 🔹 Chart / States */}
      {loading ? (
        <p className="text-center text-gray-500 py-6">Loading data...</p>
      ) : error ? (
        <p className="text-center text-red-500 py-6">{error}</p>
      ) : data.length === 0 ? (
        <p className="text-center text-gray-500 py-6">No data found.</p>
      ) : (
        <>
          <div className="h-56 overflow-x-auto">
            <div className="min-w-[900px] h-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    interval={0}
                    label={{ value: "Day", position: "insideBottom", offset: -5 }}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {userRole === 3 || userRole === 4 ? (
                    <Bar
                      dataKey="registered_users"
                      name="Registered Users"
                      fill={BRAND.blue}
                      barSize={10}
                      radius={[6, 6, 0, 0]}
                    />
                  ) : (
                    <>
                      <Bar
                        dataKey="recommended"
                        name="Recommended"
                        fill={BRAND.blue}
                        radius={[6, 6, 0, 0]}
                      />
                      <Bar
                        dataKey="chalte_chalte"
                        name="Chalte Chalte"
                        fill={BRAND.darkBlue}
                        radius={[6, 6, 0, 0]}
                      />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 🔹 Summary */}
          {summary && (
            <div className="grid grid-cols-3 text-center mt-3 text-sm text-gray-700">
              {userRole === 3 || userRole === 4 ? (
                <div className="col-span-3">
                  <p className="font-semibold text-blue-600">
                    {summary.total_registered}
                  </p>
                  <p>Total Registered Users</p>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-semibold text-blue-600">
                      {summary.total_recommended}
                    </p>
                    <p>Recommended</p>
                  </div>
                  <div>
                    <p className="font-semibold text-indigo-600">
                      {summary.totale_chalte_chalte}
                    </p>
                    <p>Chalte Chalte</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-600">
                      {summary.total}
                    </p>
                    <p>Total Customers</p>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}